import React from 'react';
import { FiPhone, FiMessageCircle, FiStar, FiCheck, FiAward, FiArrowRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Hero = () => {
  return (
    <section className="relative lg:min-h-screen bg-white overflow-hidden pt-0 lg:pt-0">
      {/* Subtle background gradient (desktop only) */}
      <div className="hidden lg:block absolute inset-0 bg-gradient-to-br from-emerald-50/40 via-white to-blue-50/30 pointer-events-none" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-6 items-center py-12 lg:py-20 lg:pt-5 lg:gap-20">
          
          {/* Left Content - Premium Typography */}
          <div className="space-y-4 z-10 order-2 lg:order-1">
            {/* Micro-copy eyebrow */}
            <div className="inline-flex items-center space-x-2 bg-emerald-50/60 backdrop-blur-sm border border-emerald-200/50 rounded-full px-4 py-2">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-emerald-700">Premium Non-Surgical Wellness</span>
            </div>

            {/* Main Headline - Clean & Modern */}
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-[1.15] tracking-tight">
                Transform Your
                <span className="block bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  Health Naturally
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl text-gray-600 font-light max-w-md leading-relaxed">
                Experience India's most trusted holistic healing approach—without surgery, without medication, with proven results.
              </p>
            </div>

            {/* Trust Indicators - Clean Layout */}
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-4 sm:gap-6">
                <div className="space-y-2">
                  <div className="text-3xl sm:text-4xl font-bold text-emerald-600">15+</div>
                  <p className="text-sm text-gray-600 font-medium">Years Experience</p>
                </div>
                <div className="space-y-2">
                  <div className="text-3xl sm:text-4xl font-bold text-emerald-600">30K+</div>
                  <p className="text-sm text-gray-600 font-medium">Lives Transformed</p>
                </div>
                <div className="space-y-2">
                  <div className="text-3xl sm:text-4xl font-bold text-emerald-600">4.9★</div>
                  <p className="text-sm text-gray-600 font-medium">Google Rated</p>
                </div>
              </div>
            </div>

            {/* Certification Badges - Minimalist */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center space-x-1.5 text-sm font-medium text-gray-700">
                <SafeIcon icon={FiCheck} className="w-4 h-4 text-emerald-600" />
                <span>No Surgery</span>
              </div>
              <div className="w-1 h-1 bg-gray-300 rounded-full" />
              <div className="flex items-center space-x-1.5 text-sm font-medium text-gray-700">
                <SafeIcon icon={FiCheck} className="w-4 h-4 text-emerald-600" />
                <span>No Medicine</span>
              </div>
              <div className="w-1 h-1 bg-gray-300 rounded-full" />
              <div className="flex items-center space-x-1.5 text-sm font-medium text-gray-700">
                <SafeIcon icon={FiCheck} className="w-4 h-4 text-emerald-600" />
                <span>100% Natural</span>
              </div>
            </div>

            {/* CTA Buttons - Premium Minimalism */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button className="group relative bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl overflow-hidden">
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Book Free Consultation
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
              <div className="flex gap-3">
                <a href="tel:+919876543210" 
                   className="flex items-center justify-center space-x-2 bg-gray-50 hover:bg-gray-100 text-gray-900 px-6 py-4 rounded-full font-semibold border border-gray-200 hover:border-gray-300 transition-all duration-300">
                  <SafeIcon icon={FiPhone} className="w-5 h-5 text-emerald-600" />
                  <span>Call</span>
                </a>
                <a href="https://wa.me/919876543210" 
                   className="flex items-center justify-center space-x-2 bg-green-50 hover:bg-green-100 text-green-700 px-6 py-4 rounded-full font-semibold border border-green-200 transition-all duration-300">
                  <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
                  <span>Chat</span>
                </a>
              </div>
            </div>
          </div>

          {/* Right Image - Premium Container */}
          <div className="relative h-full flex items-center justify-center lg:justify-end z-10 order-1 lg:order-2">
            {/* Decorative background glow (desktop only) */}
            <div className="hidden lg:block absolute inset-0 bg-gradient-to-br from-emerald-100/30 to-teal-100/20 rounded-[4rem] blur-3xl" />
            
            {/* Main Image Container */}
            <div className="relative w-full max-w-md lg:max-w-none">
              {/* Soft accent glow behind image (desktop only) */}
              <div className="hidden lg:block absolute inset-0 bg-gradient-to-br from-emerald-200/40 via-teal-100/30 to-transparent rounded-[3rem] blur-2xl" />
              
              {/* Image with premium styling */}
              <div className="relative bg-white rounded-[2.5rem] overflow-hidden shadow-2xl border border-emerald-50/50">
                <img 
                  src="/images/hero_main.jpg" 
                  alt="Dr. Wellness Expert - Premium Holistic Healing"
                  className="w-full h-64 sm:h-72 md:h-80 lg:h-[600px] object-cover"
                />
                
                {/* Subtle overlay gradient for premium feel (desktop only) */}
                <div className="hidden lg:block absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent pointer-events-none" />
              </div>

              {/* Floating Stats - Minimalist Cards */}
              <div className="hidden lg:block absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl backdrop-blur-xl border border-gray-100/50">
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-emerald-600">30K+</div>
                  <div className="text-xs text-gray-600 font-medium">Patients Healed</div>
                </div>
              </div>
              
              <div className="hidden lg:block absolute -top-6 -right-6 bg-white rounded-2xl p-4 shadow-xl backdrop-blur-xl border border-gray-100/50">
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-emerald-600">15+</div>
                  <div className="text-xs text-gray-600 font-medium">Years Excellence</div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;